import React from 'react';
import { create } from 'zustand';
import { Language, initI18n, t, isRTL, DEFAULT_LANGUAGE } from '../utils/i18n/i18n';

interface LanguageState {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string, silent?: boolean) => string;
    isRTL: boolean;
    dir: 'ltr' | 'rtl';
}

export const useLanguageStore = create<LanguageState>((set, get) => ({
    language: DEFAULT_LANGUAGE,
    setLanguage: (lang: Language) => {
        set({ language: lang });
        initI18n(lang);
    },
    t: (key: string, silent: boolean = false) => {
        const { language } = get();
        return t(key, language, silent);
    },
    isRTL: isRTL(DEFAULT_LANGUAGE),
    dir: isRTL(DEFAULT_LANGUAGE) ? 'rtl' : 'ltr',
}));

// Initialize the store with the current language
useLanguageStore.subscribe((state) => {
    // Update RTL and dir when language changes
    const newIsRTL = isRTL(state.language);
    const newDir = newIsRTL ? 'rtl' : 'ltr';

    if (state.isRTL !== newIsRTL || state.dir !== newDir) {
        useLanguageStore.setState({
            isRTL: newIsRTL,
            dir: newDir,
        });
    }
});

// Hook for components to use
export function useLanguage() {
    const store = useLanguageStore();
    return store;
}

// Provider component for SSR compatibility
export function LanguageProvider({
    children,
    initialLanguage
}: {
    children: React.ReactNode;
    initialLanguage?: Language;
}) {
    // Initialize with the provided language
    if (initialLanguage) {
        useLanguageStore.setState({ language: initialLanguage });
        initI18n(initialLanguage);
    }

    return <>{children}</>;
}
